<section class="report panel enterprise-card">
  <div class="report-head page-head">
    <div class="report-title">
      <h2>Attendance Report</h2>
      <p>Review production attendance, output, and approval status</p>
    </div>
    <div class="actions">
      <button mat-stroked-button class="report-btn" type="button" (click)="exportCsv()">
        <mat-icon>download</mat-icon>
        Export CSV / Excel
      </button>
      <button mat-flat-button color="primary" class="report-btn primary-btn" type="button" (click)="exportJson()">
        <mat-icon>data_object</mat-icon>
        Export JSON
      </button>
    </div>
  </div>

  <div class="filters toolbar-center">
    <mat-form-field appearance="outline" subscriptSizing="dynamic">
      <mat-label>Preset</mat-label>
      <mat-select [(ngModel)]="filters.preset" (selectionChange)="applyPreset($event.value)">
        <mat-option value="" disabled hidden>Select date preset</mat-option>
        <mat-option value="today">Today</mat-option>
        <mat-option value="yesterday">Yesterday</mat-option>
        <mat-option value="week">This Week</mat-option>
        <mat-option value="month">This Month</mat-option>
        <mat-option value="custom">Custom Range</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="outline" class="range-field" subscriptSizing="dynamic">
      <mat-label>Date range</mat-label>
      <mat-date-range-input [rangePicker]="picker">
        <input matStartDate placeholder="Start" [(ngModel)]="rangeStart" (dateChange)="onRangeChange()" />
        <input matEndDate placeholder="End" [(ngModel)]="rangeEnd" (dateChange)="onRangeChange()" />
      </mat-date-range-input>
      <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
      <mat-date-range-picker #picker></mat-date-range-picker>
    </mat-form-field>
    <mat-form-field appearance="outline" subscriptSizing="dynamic">
      <mat-label>Worker</mat-label>
      <mat-select [(ngModel)]="filters.worker" (selectionChange)="load()">
        <mat-option value="">All workers</mat-option>
        <mat-option *ngFor="let worker of workers" [value]="worker._id">{{ worker.name }} ({{ worker.employeeId }})</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="outline" subscriptSizing="dynamic">
      <mat-label>Machine</mat-label>
      <mat-select [(ngModel)]="filters.machine" (selectionChange)="load()">
        <mat-option value="">All machines</mat-option>
        <mat-option *ngFor="let machine of machines" [value]="machine._id">{{ machine.machineId }}</mat-option>
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="outline" subscriptSizing="dynamic">
      <mat-label>Status</mat-label>
      <mat-select [(ngModel)]="filters.status" (selectionChange)="load()">
        <mat-option value="">All statuses</mat-option>
        <mat-option value="Checked In">In Progress</mat-option>
        <mat-option value="Completed">Completed</mat-option>
        <mat-option value="Pending Supervisor Review">Pending Supervisor Review</mat-option>
        <mat-option value="Approved">Approved</mat-option>
      </mat-select>
    </mat-form-field>
    <button mat-stroked-button class="report-btn clear-btn" type="button" (click)="clearFilters()" style="margin-top: auto">
      <mat-icon>close</mat-icon>
      Clear
    </button>
  </div>

  <section class="overview-grid" *ngIf="rows.length" aria-label="Attendance report overview">
    <article class="overview-card" *ngFor="let card of overviewCards(); trackBy: trackByOverviewLabel" [ngClass]="'tone-' + card.tone">
      <span class="overview-icon"><mat-icon>{{ card.icon }}</mat-icon></span>
      <div class="overview-copy">
        <span>{{ card.label }}</span>
        <strong>{{ formatOverviewValue(card.value) }}</strong>
      </div>
      <small>{{ card.meta }}</small>
    </article>
  </section>

  <div class="empty-state" *ngIf="!rows.length">
    <mat-icon>inbox</mat-icon>
    <div>No report data available</div>
    <p>Try adjusting the filters.</p>
  </div>

  <div class="table-wrap" *ngIf="rows.length">
    <table>
      <thead>
        <tr>
          <th>Worker</th>
          <th>Machine</th>
          <th>Status</th>
          <th>Target</th>
          <th>Produced</th>
          <th>Rejected</th>
          <th>Rejection Reason</th>
          <th>Reason</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of pagedRows">
          <td>
            <span class="employee-cell">
              <span class="employee-avatar" [ngClass]="workerAvatarClass(row)">{{ workerInitials(row) }}</span>
              {{ displayCell(row.worker?.name) }}
            </span>
          </td>
          <td>{{ displayCell(row.machine?.machineId) }}</td>
          <td>
            <span class="status-badge" [ngClass]="statusClass(row.status)">
              <mat-icon>{{ statusIcon(row.status) }}</mat-icon>
              <span>{{ displayCell(row.status) }}</span>
            </span>
          </td>
          <td>{{ displayCell(row.target) }}</td>
          <td>{{ displayCell(row.produced) }}</td>
          <td>{{ displayCell(row.rejected) }}</td>
          <td>{{ displayCell(row.rejectionReason) }}</td>
          <td>{{ displayCell(row.reason) }}</td>
          <td>{{ displayDate(row.updatedAt) }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="report-paginator" *ngIf="rows.length">
    <span>{{ paginationLabel() }}</span>
    <div class="page-size-control">
      <span>Rows per page</span>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-select [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)">
          <mat-option *ngFor="let option of pageSizeOptions" [value]="option">{{ option }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <div class="pager-buttons" aria-label="Table pagination">
      <button mat-icon-button type="button" [disabled]="!canPreviousPage()" (click)="firstPage()" aria-label="First page">
        <mat-icon>keyboard_double_arrow_left</mat-icon>
      </button>
      <button mat-icon-button type="button" [disabled]="!canPreviousPage()" (click)="previousPage()" aria-label="Previous page">
        <mat-icon>chevron_left</mat-icon>
      </button>
      <span class="page-number">{{ currentPage() }}</span>
      <button mat-icon-button type="button" [disabled]="!canNextPage()" (click)="nextPage()" aria-label="Next page">
        <mat-icon>chevron_right</mat-icon>
      </button>
      <button mat-icon-button type="button" [disabled]="!canNextPage()" (click)="lastPage()" aria-label="Last page">
        <mat-icon>keyboard_double_arrow_right</mat-icon>
      </button>
    </div>
  </div>
</section>
